import React, { useEffect, useState } from "react";

import { Grid, Box } from "@mui/material";
import { Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { decodeToken, isExpired } from "react-jwt";
import { useHistory } from "react-router-dom";

import { Button } from "../../../components/Form";
import { Color } from "../../../components/Form/Button";
import { PublicFooter } from "../../../components/PublicFooter";
import { AppConfig } from "../../../constants";
import { CustomerInvitePayload } from "../../../models/customer";
import CompanyService from "../../../services/company.service";
import SetPassword from "./SetPassword";
import useStyles from "./styles";

const linkExpired = require("../../../assets/img/link-expired.svg");
const rafiki = require("../../../assets/img/rafiki-user-invite.svg");

type DecodedProps = {
  companyId: number;
  customerId: number;
};

const AcceptOrDeclineInvite = () => {
  const history = useHistory();

  const { t: translate } = useTranslation();

  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  const [inviteDeclined, setInviteDeclined] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>();
  const [customerId, setCustomerId] = useState<number>();
  const [newInvitationHasAlreadyBeenMade, setNewInvitationHasAlreadyBeenMade] =
    useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [isInvalidToken, setIsInvalidToken] = useState<boolean>(false);
  const [isExpiredToken, setIsExpiredToken] = useState<boolean>(false);
  const [showPasswordPage, setShowPasswordPage] = useState<boolean>(false);

  const classes = useStyles({
    isExpiredOrInvalidOrRejected: inviteDeclined || isInvalidToken || isExpiredToken,
  });

  const {
    mutate: inviteCustomer,
    isLoading: isLoadingInviteCustomer,
    isSuccess: customerInvited,
  } = useMutation(["company.invite.customer.user"], (customerPayload: CustomerInvitePayload) =>
    CompanyService.inviteCustomerInCompany(customerPayload)
  );

  const handleAcceptInvite = () => {
    setShowPasswordPage(true);
  };

  const handleRejectInvite = () => {
    setInviteDeclined(true);
    if (companyId && customerId) inviteCustomer({ customerId, action: "decline", companyId });
  };

  const handleInviteUserAgain = () => {
    if (!isLoadingInviteCustomer && !newInvitationHasAlreadyBeenMade && companyId && customerId) {
      setNewInvitationHasAlreadyBeenMade(true);
      inviteCustomer({ customerId, action: "resend", companyId });
    }
  };

  const handleResendInvite = () => {
    if (companyId && customerId) inviteCustomer({ customerId, action: "resend", companyId });
  };

  const getTitle = (): string => {
    if (inviteDeclined) {
      return translate("inviteDeclined");
    }

    if (isInvalidToken) {
      return `${translate("sorryTheLinkIsBroken")} :(`;
    }

    if (isExpiredToken) {
      return translate("sorryTheLinkHasExpired");
    }

    return translate("companyInvitation");
  };

  const renderDescription = (): JSX.Element => {
    const dataCy = "description";

    if (isInvalidToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkIsBroken")}
        </p>
      );
    }

    if (isExpiredToken) {
      return (
        <p className={classes.invitationDescriptionExpired} data-cy={dataCy}>
          {translate("theInviteLinkHasExpiredYouCanRequestANewOneByClickingOnTheButtonBelow")}
        </p>
      );
    }

    if (inviteDeclined) {
      return (
        <>
          <p className={classes.invitationDescription} data-cy={dataCy}>
            {translate("weWillNotifyTheAdministrator")}
          </p>
          <p className={classes.invitationDescription}>{translate("youCanCloseThisWindow")}</p>
        </>
      );
    }

    return (
      <p className={classes.invitationDescription} data-cy={dataCy}>
        {translate("youHaveBeenInvitedToBePartOf")}
        {"   "}
        <span>{translate("supplyChain")}</span>{" "}
        {translate("youCanAcceptOrDeclineTheInvitationAndWeWillNotifyTheCompanyAdministrator")}
      </p>
    );
  };

  const renderActions = (): JSX.Element => {
    if (isExpiredToken) {
      return (
        <Button
          onClick={handleResendInvite}
          loading={isLoadingInviteCustomer}
          disabled={isLoadingInviteCustomer}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="resend-button"
        >
          {translate("resendInviteLink")}
        </Button>
      );
    }

    if (isInvalidToken) {
      return (
        <Button
          onClick={() => {
            history.replace("/");
          }}
          customColor={Color.SECONDARY}
          className={classes.actionButton}
          data-cy="go-back-button"
        >
          {translate("goBackToLoginPage")}
        </Button>
      );
    }

    if (inviteDeclined) {
      return (
        <p className={classes.invitationDescriptionDeclined} data-cy="description">
          {translate("ifYouRejectedThisInvitationByMistakePleaseRequestANewInvite")}{" "}
          <a href="#" onClick={handleInviteUserAgain} data-cy="invite-user-again">{`${translate(
            "here"
          )}.`}</a>
        </p>
      );
    }

    return (
      <>
        <Button
          onClick={handleRejectInvite}
          fullWidth
          loading={isLoadingInviteCustomer}
          disabled={isLoadingInviteCustomer}
          customColor={Color.ERROR}
          className={classes.actionButton}
          data-cy="decline-button"
        >
          {translate("decline")}
        </Button>

        <Button
          onClick={handleAcceptInvite}
          fullWidth
          disabled={isLoadingInviteCustomer}
          loading={isLoadingInviteCustomer}
          customColor={Color.SUCCESS}
          className={classes.actionButton}
          data-cy="accept-button"
        >
          {translate("accept")}
        </Button>
      </>
    );
  };

  useEffect(() => {
    if (parsed?.token) {
      setToken(parsed.token);
    } else {
      history.replace("/");
    }
  }, [parsed]);

  useEffect(() => {
    if (token) {
      const decodedToken = decodeToken<DecodedProps>(token);
      const tokenIsExpired = isExpired(token);

      if (!decodedToken?.companyId || !decodedToken?.customerId) {
        setIsInvalidToken(true);

        return;
      }

      if (tokenIsExpired) {
        setIsExpiredToken(true);
      }

      setCompanyId(decodedToken.companyId);
      setCustomerId(decodedToken.customerId);
    }
  }, [token]);

  const handleSubmit = (passwordEncrypted: string) => {
    if (companyId && customerId)
      inviteCustomer({ customerId, action: "accept", companyId, password: passwordEncrypted });
  };

  if (showPasswordPage) {
    return (
      <SetPassword
        showCompletedPage={customerInvited}
        loading={isLoadingInviteCustomer}
        handleSubmitPassword={handleSubmit}
      />
    );
  }

  return (
    <div className={classes.container}>
      <Grid className={classes.containerGrid} container direction="row">
        <Grid item xs={12} sm={6}>
          <Box className={classes.asideLeft}>
            <div className={classes.sectionAsideLeft}>
              <img
                src={AppConfig.appLogo}
                alt="session-logo"
                data-cy="sessionLogo"
                className={classNames("img-fluid", classes.shipeeziLogo)}
              />

              <div className={classes.containerRafiki}>
                <div className={classes.sectionRafiki}>
                  <img
                    src={isInvalidToken || isExpiredToken ? linkExpired : rafiki}
                    className={classes.rafiki}
                    data-cy={isInvalidToken || isExpiredToken ? "link-expired" : "rafiki-invite"}
                  />
                </div>
              </div>
            </div>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            className={classes.asideRight}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box className={classes.containerAsideRight}>
              <Stack
                className={classes.sectionInvitation}
                justifyContent="center"
                alignItems="flex-start"
                spacing={1}
              >
                <h2 className={classes.title} data-cy="title">
                  {getTitle()}
                </h2>

                <Stack width="100%" justifyContent="center" alignItems="flex-start" spacing={1}>
                  {renderDescription()}
                </Stack>

                <Stack
                  width="100%"
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  {renderActions()}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <PublicFooter />
    </div>
  );
};

export default AcceptOrDeclineInvite;
